import { Button, Form, Modal, Typography } from 'antd';
import { useInvoiceCFEFormContext } from '../../InvoiceCFEFormProvider';
import { useTaxCouponGenerateTx2 } from '@/core/domain/coupon/coupons/coupons.hooks';
import { FloatingFormItem } from '@/components/_common';
import CompanySatEquipmentsSelect from '@/components/Companies/CompanySatEquipmentsSelect';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import { useManagerCouponSend } from '@/core/domain/coupon/managerCoupon/managerCoupon.hooks';
import { TaxCouponStatus } from '@/core/domain/coupon/coupons/coupons.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

const InvoiceCFESend: React.FC = () => {
  const { taxCoupon } = useInvoiceCFEFormContext();

  const [modalOpen, modalAction] = useDisclosure(false);

  const [form] = Form.useForm<{ equipmentId: string }>();
  const ruleRequired = useDefaultEmptyFieldError();

  const generateTx2 = useTaxCouponGenerateTx2(false);
  const managerCouponSend = useManagerCouponSend(taxCoupon?.id);

  async function sendConfirm() {
    try {
      const { equipmentId } = await form.validateFields();

      if (!taxCoupon?.payments.length) {
        Modal.warn({
          title: 'Dados de pagamento',
          content: 'É necessário informar pelo menos uma forma de pagamento.',
          okText: 'Fechar',
        });

        throw new Error();
      }

      if (!taxCoupon?.couponItems.length) {
        Modal.warn({
          title: 'Itens do cupom',
          content: 'É necessário informar pelo menos um item do cupom fiscal.',
          okText: 'Fechar',
        });

        throw new Error();
      }

      if (taxCoupon) {
        const tx2 = await generateTx2.mutateAsync({
          couponId: taxCoupon?.id,
          equipmentId,
        });

        await managerCouponSend.mutateAsync({ tx2 });

        modalAction.close();
      }
    } catch {}
  }

  return (
    <>
      <Button
        type="primary"
        onClick={modalAction.open}
        disabled={
          taxCoupon?.status === TaxCouponStatus.EMITTED ||
          !hasPermissions([Authorities.FISCAL_COUPON_EMISSION])
        }
      >
        Emitir CF-e
      </Button>

      <Modal
        open={modalOpen}
        onCancel={modalAction.close}
        title="Emitir cupom fiscal"
        okText="Emitir"
        onOk={sendConfirm}
        confirmLoading={generateTx2.isLoading || managerCouponSend.isLoading}
        destroyOnClose
      >
        <p>
          Tem certeza que deseja emitir o cupom para o programa de validação de
          CF-e?
        </p>

        <Form form={form}>
          <FloatingFormItem
            name="equipmentId"
            label="Selecione um equipamento"
            rules={[ruleRequired]}
            extra={
              <Typography.Link
                href={`/app/registry/companies/${taxCoupon?.companyId}/coupon`}
                target="_blank"
              >
                Gerenciar equipamentos
              </Typography.Link>
            }
          >
            <CompanySatEquipmentsSelect companyId={taxCoupon?.companyId} />
          </FloatingFormItem>
        </Form>

        <p style={{ lineHeight: 1 }}>
          <small>
            Este modo enviará o arquivo tx2 para o programa Manager SAAS de
            forma automática, não será necessário fazer o download do arquivo e
            mover para a pasta do programa.
          </small>
        </p>
      </Modal>
    </>
  );
};

export default InvoiceCFESend;
